import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LaneId } from '../../api/laneApi';
import {
  filtersFromSearchParams, applyQuery, applyFilter, applyTab, applyArchived, RECEIVED_FROM_OFF, LaneTab,
} from './laneUrlState';
import {
  parseOmniQuery, isoToUkDisplay, OmniChip, OmniChipKey,
} from './parseOmniQuery';
import './lanes.css';

/**
 * Typed-operator search box for the lane shell (Tasks Rebuild Task 13).
 * Replaces LaneShell's plain `SearchInput` slot. Reads/writes the URL
 * directly via its own `useSearchParams()` call rather than being handed
 * filter state as props -- the same "each component owns its own slice of
 * URL state" pattern every lane page (ClientLane/LenderLane/PostLane/
 * ManualLane) already uses for its lane-specific params, so this is
 * consistent rather than a new architecture.
 *
 * Enter submits the WHOLE box: `parseOmniQuery` splits it into recognised
 * operator chips (from/lender/claim/since/until/assigned/status/archived)
 * plus a free-text remainder. Each recognised chip is written into
 * laneUrlState for its own key -- but submission is MERGE, not replace: a
 * chip found in this submission overwrites that filter's prior value; a
 * filter simply not mentioned in the current box text is left untouched
 * (only the free-text `q` param is a full replace every time, since
 * whatever's left visible in the box after a submit genuinely *is* the
 * whole free-text query). This matters because recognised chips are
 * stripped out of the input after each submit (see `handleSubmit` setting
 * `inputValue` to just `parsed.freeText`) -- if a bare re-submit of
 * leftover free text also wiped every chip not currently visible in the
 * box, there would be no way to add one more free-text word without
 * silently dropping an existing `claim:`/`from:` filter. Chips are instead
 * removed explicitly via their own <> button below the input, which is the
 * mechanism the Task 13 brief calls out for chip removal.
 */

interface OperatorHint {
  operator: string;
  hint: string;
}

const OPERATOR_HINTS: OperatorHint[] = [
  { operator: 'from:', hint: 'Sender email or name, e.g. from:"John Smith"' },
  { operator: 'lender:', hint: 'Alias for from: -- filter by lender name' },
  { operator: 'claim:', hint: 'Claim reference, e.g. claim:CL12345' },
  { operator: 'since:', hint: 'Received on/after a UK date, e.g. since:01/07/2026' },
  { operator: 'until:', hint: 'Received on/before a UK date, e.g. until:31/12/2026' },
  { operator: 'assigned:', hint: 'Assignee name, or assigned:unassigned' },
  { operator: 'status:', hint: 'Switch tab -- status:pending or status:completed' },
  { operator: 'archived:yes', hint: 'Search archived tasks' },
];

/** Short, corrective one-liners shown when a recognised operator prefix is
 * typed with an invalid value (Item 13). Deliberately terser than the full
 * `OPERATOR_HINTS` copy above -- these are error feedback, not discovery
 * hints, so they name the fix ("needs a date like 1/7") rather than the
 * feature. Keyed by the normalised chip key `parseOmniQuery` reports on each
 * `invalidTokens` entry (e.g. an invalid `lender:` reports key `from`). */
const INVALID_TOKEN_HINTS: Record<OmniChipKey, string> = {
  from: 'from: needs a sender, e.g. from:john@x.com',
  claim: 'claim: needs a reference, e.g. claim:CL12345',
  since: 'since: needs a date like 1/7',
  until: 'until: needs a date like 1/7',
  assigned: 'assigned: needs a name, or assigned:unassigned',
  status: 'status: must be pending or completed',
  archived: 'archived: only accepts yes',
};

/** Last whitespace-delimited chunk of the input, lowercased -- used to
 * narrow the suggestions list to operators matching what's being typed
 * right now (e.g. "fr" -> from:). Empty once the user has finished a token
 * (trailing space), which intentionally falls back to showing every hint. */
function currentToken(value: string): string {
  const idx = value.lastIndexOf(' ');
  return (idx === -1 ? value : value.slice(idx + 1)).toLowerCase();
}

interface ActiveChip {
  chipKey: string;
  label: string;
  tooltip?: string;
  muted?: boolean;
  onRemove: () => void;
}

export interface OmniboxProps {
  lane: LaneId;
}

export const Omnibox: React.FC<OmniboxProps> = ({ lane }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = filtersFromSearchParams(searchParams);

  const [inputValue, setInputValue] = useState(filters.q ?? '');
  const [open, setOpen] = useState(false);
  // Roving highlight for keyboard navigation of the suggestions dropdown
  // (Item 11). -1 == nothing highlighted (Enter then submits the form as
  // normal); >= 0 indexes into `visibleHints`. Kept as state (not DOM
  // focus): this is the standard combobox pattern where focus stays in the
  // input and the active option is advertised via aria-activedescendant.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimer = useRef<number | null>(null);

  // Keeps the box in sync with the URL on back/forward navigation or a
  // shared/deep link -- same idiom LaneShell used to own for the plain
  // SearchInput it's replacing.
  useEffect(() => { setInputValue(filters.q ?? ''); }, [filters.q]);

  useEffect(() => () => {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
  }, []);

  // Reset the roving highlight whenever the typed token changes (which
  // rebuilds `visibleHints`, so a stale index could point at the wrong or a
  // now-absent suggestion). Arrow-key presses don't change `inputValue`, so
  // they never trip this -- the highlight they set survives until the user
  // types again, selects, or closes the dropdown. Open/close-driven resets
  // are handled inline in their handlers (not here) so ArrowDown can open
  // the dropdown and set an index in the same event without this effect
  // immediately clobbering it.
  useEffect(() => { setHighlightedIndex(-1); }, [inputValue]);

  const applyChip = (params: URLSearchParams, chip: OmniChip): URLSearchParams => {
    switch (chip.key) {
      case 'from': return applyFilter(params, 'senderEmail', chip.value);
      case 'claim': return applyFilter(params, 'claim', chip.value);
      case 'since': return applyFilter(params, 'receivedFrom', chip.value);
      case 'until': return applyFilter(params, 'until', chip.value);
      case 'assigned': return applyFilter(params, 'assigned', chip.value);
      case 'status': return applyTab(params, chip.value as LaneTab);
      case 'archived': return applyArchived(params, true);
      default: return params;
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const parsed = parseOmniQuery(inputValue);
    setSearchParams((prev) => {
      let next = applyQuery(prev, parsed.freeText);
      parsed.chips.forEach((chip) => { next = applyChip(next, chip); });
      return next;
    });
    setInputValue(parsed.freeText);
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    if (e.key === 'ArrowDown') {
      // Open on first ArrowDown, then advance (clamped at the last hint).
      // preventDefault stops the input caret jumping to end-of-text.
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((i) => Math.min(i + 1, visibleHints.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      // With a suggestion highlighted, Enter selects it (inserts the
      // operator) and must NOT fall through to form submission -- so
      // preventDefault. This never blurs the input, so it can't be swallowed
      // by the onBlur close timer (which only fires on a genuine focus
      // loss -- Tab away or a click outside; mousedown-select is already
      // guarded). With nothing highlighted (index -1) we intentionally do
      // NOT preventDefault, letting the form submit exactly as before.
      if (open && highlightedIndex >= 0 && highlightedIndex < visibleHints.length) {
        e.preventDefault();
        insertOperator(visibleHints[highlightedIndex].operator);
      }
    }
  };

  const insertOperator = (operator: string) => {
    setInputValue((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${operator}`);
    setOpen(true);
    inputRef.current?.focus();
  };

  const token = currentToken(inputValue);
  const tokenPrefix = token.split(':')[0];
  const filteredHints = tokenPrefix
    ? OPERATOR_HINTS.filter((h) => h.operator.startsWith(tokenPrefix))
    : OPERATOR_HINTS;
  const visibleHints = filteredHints.length > 0 ? filteredHints : OPERATOR_HINTS;

  // Item 13: re-parse the live input every render (pure/synchronous, no
  // network -- cheap enough to run per keystroke, no debounce needed) to
  // surface inline feedback for recognised operators typed with an invalid
  // value (`since:31/13`, `status:bogus`). Genuinely unknown prefixes
  // (`foo:bar`) don't appear here -- they're correct free text. Deduped to
  // one hint per operator key so `since:31/13 since:9/9` shows a single
  // since: hint. Because it's derived purely from `inputValue`, it clears
  // itself the moment the offending token is fixed or removed -- no separate
  // dismiss state needed.
  const invalidTokenKeys: OmniChipKey[] = [];
  parseOmniQuery(inputValue).invalidTokens.forEach((t) => {
    if (!invalidTokenKeys.includes(t.key)) invalidTokenKeys.push(t.key);
  });

  // Active-filter chips reflect the CURRENT URL state (not just what was
  // typed in this box) -- e.g. on the Lender lane, `receivedFrom` may have
  // been set via its own since-01/07/2026 default chip rather than a
  // since: operator, and it should still show here. The Lender lane
  // already renders its own bespoke "Only since X" pill (with the
  // default-chip dismiss/re-add affordance) via LaneShell's `renderChips`
  // slot, so this generic row deliberately skips `since` there to avoid
  // showing two chips for the one underlying `receivedFrom` field --
  // elsewhere (Client/Post/Manual), which have no equivalent UI at all,
  // this is the only removal affordance for a since: filter. Either way
  // both read/write the exact same `receivedFrom` URL param via the same
  // `RECEIVED_FROM_OFF` sentinel LenderLane's dismiss button uses -- no
  // parallel state, just an optional second view onto it.
  const activeChips: ActiveChip[] = [];
  if (filters.senderEmail) {
    activeChips.push({
      chipKey: 'from',
      label: `From: ${filters.senderEmail}`,
      onRemove: () => setSearchParams((p) => applyFilter(p, 'senderEmail', null)),
    });
  }
  if (filters.claim) {
    activeChips.push({
      chipKey: 'claim',
      label: `Claim: ${filters.claim}`,
      onRemove: () => setSearchParams((p) => applyFilter(p, 'claim', null)),
    });
  }
  if (filters.receivedFrom && lane !== 'lender') {
    activeChips.push({
      chipKey: 'since',
      label: `Since: ${isoToUkDisplay(filters.receivedFrom)}`,
      // T13 review follow-up 4a: receivedFrom is a backend no-op against
      // archived_email_tasks (task_lane_service._build_common_filters only
      // applies it `and not archived`) -- mute it the same way `until` is
      // muted, rather than silently implying it's still narrowing results.
      ...(filters.archived
        ? { muted: true, tooltip: "Not applied -- archived browsing doesn't support a received-date filter." }
        : {}),
      // Sentinel-aware removal (matches LenderLane's dismissReceivedFrom):
      // deleting the param outright would read back as "never touched",
      // which would immediately re-default on any lane that applies the
      // same since-default convention -- setting the 'off' sentinel
      // durably records the dismissal instead.
      onRemove: () => setSearchParams((p) => applyFilter(p, 'receivedFrom', RECEIVED_FROM_OFF)),
    });
  }
  if (filters.until) {
    activeChips.push({
      chipKey: 'until',
      label: `Until: ${isoToUkDisplay(filters.until)}`,
      // #4: until: is now a real backend filter (received_date < until + 1
      // day, task_lane_service.py) -- active by default, same as `since`
      // and `assigned` above. It's a no-op under archived:yes for the same
      // reason those two are (_build_common_filters applies it `and not
      // archived`, archived_email_tasks has no received_date column), so it
      // gets the identical muted/tooltip treatment there.
      ...(filters.archived
        ? { muted: true, tooltip: "Not applied -- archived browsing doesn't support a received-date filter." }
        : {}),
      onRemove: () => setSearchParams((p) => applyFilter(p, 'until', null)),
    });
  }
  if (filters.assigned) {
    activeChips.push({
      chipKey: 'assigned',
      label: `Assigned: ${filters.assigned === 'unassigned' ? 'Unassigned' : filters.assigned}`,
      // T13 review follow-up 4a: same backend no-op as `since` above --
      // `assigned` is also only applied `and not archived`.
      ...(filters.archived
        ? { muted: true, tooltip: "Not applied -- archived browsing doesn't support an assignee filter." }
        : {}),
      onRemove: () => setSearchParams((p) => applyFilter(p, 'assigned', null)),
    });
  }
  if (filters.archived) {
    activeChips.push({
      chipKey: 'archived',
      label: 'Archived',
      onRemove: () => setSearchParams((p) => applyArchived(p, false)),
    });
  }
  if (filters.tab === 'completed') {
    activeChips.push({
      chipKey: 'status',
      label: 'Status: completed',
      onRemove: () => setSearchParams((p) => applyTab(p, 'pending')),
    });
  }

  return (
    <div className="omnibox">
      <form className="omnibox__form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="search"
          className="omnibox__input"
          value={inputValue}
          placeholder="Search... try from:, claim:, since:, assigned:"
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => { setOpen(true); setHighlightedIndex(-1); }}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => {
              setOpen(false);
              setHighlightedIndex(-1);
            }, 150);
          }}
          onKeyDown={handleKeyDown}
          aria-label="Search tasks"
          // Accessible combobox wiring (Item 11). Role stays the implicit
          // `searchbox` (type=search) -- deliberately NOT overridden to
          // `combobox`, both to keep existing getByRole('searchbox') queries
          // working and because aria-activedescendant conveys the active
          // option without a role change. The highlighted <li>'s id is
          // advertised here rather than moving DOM focus onto it.
          aria-controls="omnibox-suggestions-list"
          aria-activedescendant={
            open && highlightedIndex >= 0 && highlightedIndex < visibleHints.length
              ? `omnibox-suggestion-${visibleHints[highlightedIndex].operator}`
              : undefined
          }
        />
        {open && (
          <ul
            className="omnibox__suggestions"
            id="omnibox-suggestions-list"
            role="listbox"
            aria-label="Search operators"
          >
            {visibleHints.map((h, idx) => {
              const active = idx === highlightedIndex;
              return (
                <li
                  key={h.operator}
                  id={`omnibox-suggestion-${h.operator}`}
                  role="option"
                  aria-selected={active}
                >
                  <button
                    type="button"
                    className={`omnibox__suggestion${active ? ' omnibox__suggestion--active' : ''}`}
                    // Keep focus in the input on a mouse-select so the box
                    // never blurs -- this is also what makes the keyboard
                    // path's Enter-select immune to the onBlur close timer.
                    onMouseDown={(e) => { e.preventDefault(); insertOperator(h.operator); }}
                    // Hovering syncs the roving highlight so mouse and
                    // keyboard don't show two different active rows.
                    onMouseEnter={() => setHighlightedIndex(idx)}
                  >
                    <span className="omnibox__suggestion-operator">{h.operator}</span>
                    <span className="omnibox__suggestion-hint">{h.hint}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </form>
      {invalidTokenKeys.length > 0 && (
        <div className="omnibox__invalid-hint" role="alert">
          {invalidTokenKeys.map((key) => (
            <span key={key} className="omnibox__invalid-hint-item">{INVALID_TOKEN_HINTS[key]}</span>
          ))}
        </div>
      )}
      {activeChips.length > 0 && (
        <div className="omnibox__chips">
          {activeChips.map((chip) => (
            <span
              key={chip.chipKey}
              className={`omnibox__chip ${chip.muted ? 'omnibox__chip--muted' : ''}`}
              title={chip.tooltip}
            >
              {chip.label}
              <button
                type="button"
                className="omnibox__chip-remove"
                aria-label={`Remove ${chip.label} filter`}
                onClick={chip.onRemove}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default Omnibox;
